<p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

<div class="mfa-page" [class.mfa-page--embedded]="readonly()">
  @if (!readonly()) {
    <!-- Live region per screen reader: annuncia il cambio di step (WCAG 2.1 4.1.3) -->
    <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
      {{ liveMessage() }}
    </div>

    <section class="page-header" role="region" aria-labelledby="mfa-title">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-shield header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 id="mfa-title">Dati MFA — Codici SOL</h1>
            <p class="subtitle">
              Inserisci, per ogni codice SOL, la mail principale, il responsabile e le associazioni
              delle persone abilitate all'accesso ai sistemi gestionali AXA.
            </p>
          </div>
        </div>
        <button
          pButton
          type="button"
          label="Torna allo stato registrazione"
          icon="pi pi-arrow-left"
          class="p-button-outlined back-button"
          (click)="goToProgress()"
          aria-label="Torna alla pagina di progressione della registrazione"
        ></button>
      </div>
    </section>
  }

  @if (isLoading()) {
    <p-card>
      <p class="text-600 m-0">Caricamento dati MFA…</p>
    </p-card>
  } @else if (loadError()) {
    <p-message severity="error" styleClass="w-full">
      Impossibile caricare i dati MFA. Riprova più tardi.
    </p-message>
  } @else if (showReadonlyRecap()) {
    <!-- Vista sola lettura: gli stessi dati compilati dal fiduciario, senza wizard né azioni.
         Usata sia nell'embed Back Office (readonly) sia dal fiduciario che rivede i dati inviati. -->
    @if (sols().length === 0) {
      <p-message severity="warn" styleClass="w-full">
        Nessun codice SOL censito per questo fiduciario.
      </p-message>
    } @else {
      @if (!readonly()) {
        <p-message severity="info" styleClass="w-full mb-3">
          Stai visualizzando i dati MFA (codici SOL) inviati in modalità sola lettura.
        </p-message>
      }
      @for (sol of sols(); track sol.solCodeId) {
        <p-card styleClass="mb-3">
          <div class="sol-card-head">
            <span class="sol-code">Codice SOL {{ sol.code }}</span>
            @if (sol.denomination) { <span class="sol-denom">{{ sol.denomination }}</span> }
            @if (sol.master) { <p-tag value="Master" severity="info" /> }
            <span class="ml-auto"><p-tag [value]="statusLabel(sol.status)" severity="secondary" /></span>
          </div>
          @if (solLocation(sol)) {
            <p class="sol-location">
              <i class="pi" [ngClass]="solLocationIcon(sol)" aria-hidden="true"></i>
              <span class="visually-hidden">{{ solLocationLabel(sol) }}</span>{{ solLocation(sol) }}
            </p>
          }
          <div class="grid">
            <div class="field col-12 md:col-6">
              <small class="text-600 block mb-1 font-bold">Legale Rappresentante</small>
              <div>
                @if (sol.holderFirstName || sol.holderLastName) {
                  {{ sol.holderLastName }} {{ sol.holderFirstName }}
                  @if (sol.holderFiscalCode) { <span class="text-600">({{ sol.holderFiscalCode }})</span> }
                } @else {
                  —
                }
              </div>
            </div>
            <div class="field col-12 md:col-6">
              <small class="text-600 block mb-1 font-bold">Mail PERSONALE per accedere ai sistemi gestionali AXA</small>
              <div>{{ sol.principalEmail || '—' }}</div>
            </div>
          </div>
          <div class="mt-3">
            <small class="text-600 block mb-2 font-bold">Associazioni ({{ selectedCount(sol) }})</small>
            @if (selectedCount(sol) === 0) {
              <p class="assoc-cards__empty">Nessuna associazione</p>
            } @else {
              <div class="assoc-cards">
                @for (assoc of sol.associations; track assoc.collaboratorId) {
                  @if (assoc.selected) {
                    <div class="assoc-card">
                      <div class="assoc-card__person">
                        <span class="assoc-card__avatar" aria-hidden="true"><i class="pi pi-user"></i></span>
                        <div>
                          <div class="assoc-card__name">
                            {{ personFullName(assoc) }}
                            @if (assoc.fiscalCode) { <span class="assoc-card__fc">({{ assoc.fiscalCode }})</span> }
                          </div>
                          <div class="assoc-card__meta">
                            <p-tag [value]="personKindLabel(assoc)" severity="secondary" />
                            @if (personRole(assoc); as role) {
                              <span class="assoc-card__role">{{ role }}</span>
                            }
                          </div>
                        </div>
                      </div>
                      <div class="assoc-card__mail">
                        <span class="k">Mail PERSONALE per accedere ai sistemi gestionali AXA</span>
                        <span class="v">{{ assoc.accessEmail || '—' }}</span>
                      </div>
                    </div>
                  }
                }
              </div>
            }
          </div>
        </p-card>
      }
    }
  } @else if (!editable()) {
    <!-- Fase non attiva per la compilazione da parte del fiduciario -->
    <p-card>
      <div class="mfa-empty">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <h2>Compilazione non disponibile</h2>
        <p>
          La compilazione dei dati MFA non è attualmente richiesta. Verrai avvisato quando il
          Back Office aprirà la fase di compilazione.
        </p>
      </div>
    </p-card>
  } @else if (sols().length === 0) {
    <p-message severity="warn" styleClass="w-full">
      Nessun codice SOL censito per questo fiduciario. Contatta il Back Office.
    </p-message>
  } @else {
    <!-- Banner richiesta di modifica dal Back Office (stesso stile dell'anagrafica).
         Il boilerplate iniziale di "richiesta compilazione" è filtrato da displayStepNote(). -->
    @if (displayStepNote()) {
      <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
        <div class="flex align-items-start gap-3">
          <div class="flex-shrink-0">
            <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
          </div>
          <div class="flex-1">
            <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
              <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
              Richiesta di Modifica — Dati MFA
            </h3>
            <p class="text-orange-800 text-base m-0 mb-3">
              AXA ha richiesto delle modifiche ai dati MFA (codici SOL). Correggi i dati indicati e reinvia la compilazione.
            </p>
            <div class="surface-50 border-round p-3 border-left-3 border-orange-500">
              <p class="text-sm font-semibold text-600 m-0 mb-2">
                <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                Note del Revisore:
              </p>
              <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayStepNote() }}</p>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Stepper header (stesso stile del wizard BO) -->
    <ol class="mfa-steps" aria-label="Avanzamento compilazione dati MFA">
      @for (title of stepTitles; track $index) {
        <li
          class="mfa-steps__item"
          [class.is-current]="currentStepIndex() === $index"
          [class.is-done]="currentStepIndex() > $index"
          [attr.aria-current]="currentStepIndex() === $index ? 'step' : null"
        >
          <span class="mfa-steps__badge" aria-hidden="true">{{ $index + 1 }}</span>
          <span class="mfa-steps__label">{{ title }}</span>
        </li>
      }
    </ol>

    <!-- STEP 1 — Dati di accesso del Legale Rappresentante -->
    @if (currentStepIndex() === 0) {
      <p class="mfa-step-intro">
        Per ogni <strong>Codice SOL da attivare</strong> indica i dati del Legale Rappresentante (nome,
        cognome, codice fiscale) e la mail <strong>personale</strong> con cui accederà ai sistemi
        gestionali AXA. Puoi comprimere le sezioni già compilate: il badge indica se una sezione è
        completa, da compilare o in errore.
      </p>
      @for (sol of sols(); track sol.solCodeId) {
        <p-card styleClass="mb-3">
          <div
            class="surface-100 border-round p-3 border-1"
            [class.border-300]="sectionState(sol, 0) !== 'error'"
            [class.border-red-500]="sectionState(sol, 0) === 'error'"
            [class.mb-3]="isSectionOpen(sol, 0)"
          >
            <div class="flex justify-content-between align-items-center gap-2">
              <button
                type="button"
                class="flex align-items-center gap-2 flex-1 border-none bg-transparent p-0 cursor-pointer text-left"
                (click)="toggleSection(sol, 0)"
                [attr.aria-expanded]="isSectionOpen(sol, 0)"
                [attr.aria-controls]="'sec0-' + sol.solCodeId"
                [attr.aria-label]="(isSectionOpen(sol, 0) ? 'Comprimi' : 'Espandi') + ' codice SOL ' + sol.code"
              >
                <i
                  class="pi"
                  [ngClass]="isSectionOpen(sol, 0) ? 'pi-chevron-down' : 'pi-chevron-right'"
                  aria-hidden="true"
                ></i>
                <span class="flex flex-column gap-1">
                  <h4 class="text-lg font-semibold m-0 sol-code">
                    Codice SOL {{ sol.code }}
                    @if (sol.denomination) { <span class="font-normal text-600"> — {{ sol.denomination }}</span> }
                  </h4>
                  @if (sol.holderLastName || sol.holderFirstName || sol.holderFiscalCode || sol.principalEmail) {
                    <span class="text-sm font-normal text-600 sol-header-summary">
                      @if (sol.holderLastName || sol.holderFirstName) {
                        <span>{{ sol.holderLastName }} {{ sol.holderFirstName }}</span>
                      }
                      @if (sol.holderFiscalCode) { <span> · {{ sol.holderFiscalCode }}</span> }
                      @if (sol.principalEmail) {
                        <span> · <i class="pi pi-envelope" aria-hidden="true"></i> {{ sol.principalEmail }}</span>
                      }
                    </span>
                  }
                </span>
              </button>
              <span class="flex-shrink-0">
                @if (isSolStep1Editable(sol)) {
                  <p-tag
                    [value]="sectionBadgeLabel(sol, 0)"
                    [severity]="sectionBadgeSeverity(sol, 0)"
                    [styleClass]="'sec-badge sec-badge--' + sectionState(sol, 0)"
                  />
                } @else {
                  <p-tag [value]="statusLabel(sol.status)" severity="secondary" />
                }
              </span>
            </div>
          </div>

          @if (isSectionOpen(sol, 0)) {
            <div [id]="'sec0-' + sol.solCodeId" class="sol-section-body">
              @if (solLocation(sol)) {
                <p class="sol-location">
                  <i class="pi" [ngClass]="solLocationIcon(sol)" aria-hidden="true"></i>
                  <span class="visually-hidden">{{ solLocationLabel(sol) }}</span>{{ solLocation(sol) }}
                </p>
              }

              @if (!isSolStep1Editable(sol)) {
                <p class="sol-readonly-note">
                  Questo codice SOL è già <strong>{{ statusLabel(sol.status) | lowercase }}</strong>:
                  i dati non sono modificabili.
                </p>
                <dl class="sol-readonly">
                  <div><dt>Legale Rappresentante</dt><dd>{{ sol.holderLastName }} {{ sol.holderFirstName }}</dd></div>
                  @if (sol.holderFiscalCode) {
                    <div><dt>Codice fiscale</dt><dd>{{ sol.holderFiscalCode }}</dd></div>
                  }
                  <div><dt>Mail personale di accesso</dt><dd>{{ sol.principalEmail || '—' }}</dd></div>
                </dl>
              } @else {
          <p class="sol-hint">{{ mfaFieldsHint }}</p>

          <div class="grid">
            <div class="field col-12 md:col-6">
              <label [for]="'hln-' + sol.solCodeId" class="font-semibold block mb-2">
                Cognome
                <span class="req" aria-hidden="true">*</span>
                <span class="visually-hidden">obbligatorio</span>
              </label>
              <input
                pInputText
                class="w-full"
                [id]="'hln-' + sol.solCodeId"
                [(ngModel)]="sol.holderLastName"
                [attr.aria-required]="true"
                [attr.aria-invalid]="invalidHolderLastName(sol)"
                [attr.aria-describedby]="invalidHolderLastName(sol) ? 'hln-' + sol.solCodeId + '-error' : null"
                [class.ng-invalid]="invalidHolderLastName(sol)"
                [class.ng-dirty]="invalidHolderLastName(sol)"
              />
              @if (invalidHolderLastName(sol)) {
                <small [id]="'hln-' + sol.solCodeId + '-error'" class="text-red-500" role="alert">
                  Campo obbligatorio.
                </small>
              }
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'hfn-' + sol.solCodeId" class="font-semibold block mb-2">
                Nome
                <span class="req" aria-hidden="true">*</span>
                <span class="visually-hidden">obbligatorio</span>
              </label>
              <input
                pInputText
                class="w-full"
                [id]="'hfn-' + sol.solCodeId"
                [(ngModel)]="sol.holderFirstName"
                [attr.aria-required]="true"
                [attr.aria-invalid]="invalidHolderFirstName(sol)"
                [attr.aria-describedby]="invalidHolderFirstName(sol) ? 'hfn-' + sol.solCodeId + '-error' : null"
                [class.ng-invalid]="invalidHolderFirstName(sol)"
                [class.ng-dirty]="invalidHolderFirstName(sol)"
              />
              @if (invalidHolderFirstName(sol)) {
                <small [id]="'hfn-' + sol.solCodeId + '-error'" class="text-red-500" role="alert">
                  Campo obbligatorio.
                </small>
              }
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'hcf-' + sol.solCodeId" class="font-semibold block mb-2">
                Codice fiscale
                <span class="req" aria-hidden="true">*</span>
                <span class="visually-hidden">obbligatorio</span>
              </label>
              <input
                pInputText
                class="w-full cf-input"
                [id]="'hcf-' + sol.solCodeId"
                [(ngModel)]="sol.holderFiscalCode"
                maxlength="16"
                [attr.aria-required]="true"
                [attr.aria-invalid]="invalidHolderFiscalCode(sol)"
                [attr.aria-describedby]="invalidHolderFiscalCode(sol) ? 'hcf-' + sol.solCodeId + '-error' : null"
                [class.ng-invalid]="invalidHolderFiscalCode(sol)"
                [class.ng-dirty]="invalidHolderFiscalCode(sol)"
              />
              @if (invalidHolderFiscalCode(sol)) {
                <small [id]="'hcf-' + sol.solCodeId + '-error'" class="text-red-500" role="alert">
                  {{ fiscalCodeError(sol) }}
                </small>
              }
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'pe-' + sol.solCodeId" class="font-bold block mb-2 text-sm">
                Mail PERSONALE per accedere ai sistemi gestionali AXA
                <span class="req" aria-hidden="true">*</span>
                <span class="visually-hidden">obbligatorio</span>
              </label>
              <small class="block text-color-secondary mb-2">
                Indica una mail <strong>personale</strong>, riferita alla singola persona che accede.
                Non usare caselle generiche o aziendali (es. info@, ufficio@, studio@).
              </small>
              <input
                pInputText
                class="w-full"
                [id]="'pe-' + sol.solCodeId"
                type="email"
                [(ngModel)]="sol.principalEmail"
                (blur)="sol.principalEmailTouched = true"
                [attr.aria-required]="true"
                [attr.aria-invalid]="!!principalEmailError(sol)"
                [attr.aria-describedby]="principalEmailError(sol) ? 'pe-' + sol.solCodeId + '-error' : null"
                [class.ng-invalid]="!!principalEmailError(sol)"
                [class.ng-dirty]="!!principalEmailError(sol)"
                placeholder="nome@dominio.it"
              />
              @if (principalEmailError(sol); as peError) {
                <small [id]="'pe-' + sol.solCodeId + '-error'" class="text-red-500" role="alert">
                  {{ peError }}
                </small>
              } @else if (principalDuplicateWarning(sol); as peWarn) {
                <small [id]="'pe-' + sol.solCodeId + '-warn'" class="field-warning" role="status">
                  <i class="pi pi-exclamation-triangle" aria-hidden="true"></i> {{ peWarn }}
                </small>
              }
            </div>
          </div>
              }
            </div>
          }
        </p-card>
      }
    }

    <!-- STEP 2 — Associazioni -->
    @if (currentStepIndex() === 1) {
      <p class="mfa-step-intro">
        Per ogni <strong>Codice SOL da attivare</strong>, seleziona le persone (impiegati amministrativi
        e collaboratori) che devono poter accedere ai sistemi AXA e indica per ciascuna la mail di
        accesso. Chi non selezioni non riceverà alcun accesso. Questo passaggio è
        <strong>facoltativo</strong>: se per un codice SOL non serve associare nessuno, puoi proseguire
        senza selezionare alcun impiegato o collaboratore. Quando le persone sono molte, usa la
        ricerca e il filtro «solo selezionati» per facilitarti la compilazione.
      </p>
      @for (sol of sols(); track sol.solCodeId) {
        <p-card styleClass="mb-3">
          <div
            class="surface-100 border-round p-3 border-1"
            [class.border-300]="sectionState(sol, 1) !== 'error'"
            [class.border-red-500]="sectionState(sol, 1) === 'error'"
            [class.mb-3]="isSectionOpen(sol, 1)"
          >
            <div class="flex justify-content-between align-items-center gap-2">
              <button
                type="button"
                class="flex align-items-center gap-2 flex-1 border-none bg-transparent p-0 cursor-pointer text-left"
                (click)="toggleSection(sol, 1)"
                [attr.aria-expanded]="isSectionOpen(sol, 1)"
                [attr.aria-controls]="'sec1-' + sol.solCodeId"
                [attr.aria-label]="(isSectionOpen(sol, 1) ? 'Comprimi' : 'Espandi') + ' codice SOL ' + sol.code"
              >
                <i
                  class="pi"
                  [ngClass]="isSectionOpen(sol, 1) ? 'pi-chevron-down' : 'pi-chevron-right'"
                  aria-hidden="true"
                ></i>
                <h4 class="text-lg font-semibold m-0 sol-code">
                  Codice SOL {{ sol.code }}
                  @if (sol.denomination) { <span class="font-normal text-600"> — {{ sol.denomination }}</span> }
                </h4>
              </button>
              <span class="flex-shrink-0">
                @if (isSolStep2Editable(sol)) {
                  <p-tag
                    [value]="sectionBadgeLabel(sol, 1)"
                    [severity]="sectionBadgeSeverity(sol, 1)"
                    [styleClass]="'sec-badge sec-badge--' + sectionState(sol, 1)"
                  />
                } @else {
                  <p-tag [value]="statusLabel(sol.status)" severity="secondary" />
                }
              </span>
            </div>
          </div>

          @if (isSectionOpen(sol, 1)) {
            <div [id]="'sec1-' + sol.solCodeId" class="sol-section-body">
              @if (solLocation(sol)) {
                <p class="sol-location">
                  <i class="pi" [ngClass]="solLocationIcon(sol)" aria-hidden="true"></i>
                  <span class="visually-hidden">{{ solLocationLabel(sol) }}</span>{{ solLocation(sol) }}
                </p>
              }

              @if (!isSolStep2Editable(sol)) {
                <p class="sol-readonly-note">
                  Questo codice SOL è già <strong>{{ statusLabel(sol.status) | lowercase }}</strong>:
                  le associazioni non sono modificabili.
                </p>
                @if (selectedCount(sol) === 0) {
                  <p class="text-600 m-0">Nessuna persona associata.</p>
                } @else {
                  <ul class="sol-readonly-assoc">
                    @for (assoc of sol.associations; track assoc.collaboratorId) {
                      @if (assoc.selected) {
                        <li>
                          <span class="ra-name">{{ personFullName(assoc) }}</span>
                          <span class="ra-mail">
                            <small class="text-600">Mail PERSONALE per accedere ai sistemi gestionali AXA:</small>
                            {{ assoc.accessEmail || '—' }}
                          </span>
                        </li>
                      }
                    }
                  </ul>
                }
              } @else if (sol.associations.length === 0) {
                <p class="text-600 m-0">Nessun collaboratore o impiegato amministrativo disponibile.</p>
              } @else {
                <div class="assoc-toolbar">
                  <input
                    pInputText
                    type="search"
                    class="assoc-search"
                    [id]="'assoc-search-' + sol.solCodeId"
                    [(ngModel)]="sol.search"
                    placeholder="Cerca per cognome, nome o codice fiscale"
                    [attr.aria-label]="'Cerca persona nel codice SOL ' + sol.code"
                  />
                  <p-checkbox
                    [(ngModel)]="sol.onlySelected"
                    [binary]="true"
                    [inputId]="'only-sel-' + sol.solCodeId"
                  />
                  <label [for]="'only-sel-' + sol.solCodeId" class="assoc-only-label">
                    Solo selezionati
                  </label>
                  <span class="text-600 ml-auto">{{ selectedCount(sol) }} selezionati</span>
                </div>

                @if (visibleAssociations(sol).length === 0) {
                  <p class="text-600 m-0">Nessuna persona corrisponde ai filtri applicati.</p>
                } @else {
                  <div class="assoc-list">
                    @for (assoc of visibleAssociations(sol); track assoc.collaboratorId) {
                      <div class="assoc-row" [class.selected]="assoc.selected">
                  <div class="assoc-check">
                    <p-checkbox
                      [(ngModel)]="assoc.selected"
                      [binary]="true"
                      [inputId]="'assoc-' + sol.solCodeId + '-' + assoc.collaboratorId"
                      [disabled]="assoc.locked"
                      (onChange)="onAssocSelectionChange(assoc)"
                    />
                    <label [for]="'assoc-' + sol.solCodeId + '-' + assoc.collaboratorId">
                      {{ personFullName(assoc) }}
                      <span class="assoc-kind">{{ personKindLabel(assoc) }}</span>
                      @if (personRole(assoc); as role) { <span class="assoc-role">· {{ role }}</span> }
                      @if (assoc.fiscalCode) { <span class="assoc-cf">· {{ assoc.fiscalCode }}</span> }
                      @if (assoc.locked) {
                        <span class="assoc-locked" title="Associazione già validata: non modificabile">
                          <i class="pi pi-lock" aria-hidden="true"></i> Validato
                        </span>
                      }
                    </label>
                  </div>
                  <div class="assoc-email field">
                    <label
                      [for]="'assoc-mail-' + sol.solCodeId + '-' + assoc.collaboratorId"
                      class="font-bold text-sm"
                    >
                      Mail PERSONALE per accedere ai sistemi gestionali AXA
                      <span class="visually-hidden"> per {{ personFullName(assoc) }}</span>
                    </label>
                    <small class="block text-color-secondary mb-2">
                      Mail <strong>personale</strong> della persona associata, non una casella
                      generica o aziendale (es. info@, ufficio@, studio@).
                    </small>
                    <input
                      pInputText
                      class="w-full"
                      [id]="'assoc-mail-' + sol.solCodeId + '-' + assoc.collaboratorId"
                      type="email"
                      [(ngModel)]="assoc.accessEmail"
                      (blur)="assoc.accessEmailTouched = true"
                      [disabled]="!assoc.selected || assoc.locked"
                      [attr.aria-required]="assoc.selected"
                      [attr.aria-invalid]="!!accessEmailError(assoc)"
                      [attr.aria-describedby]="accessEmailError(assoc) ? 'assoc-mail-' + sol.solCodeId + '-' + assoc.collaboratorId + '-error' : null"
                      [class.ng-invalid]="!!accessEmailError(assoc)"
                      [class.ng-dirty]="!!accessEmailError(assoc)"
                      placeholder="mail di accesso ai sistemi AXA"
                    />
                    @if (accessEmailError(assoc); as aeError) {
                      <small
                        [id]="'assoc-mail-' + sol.solCodeId + '-' + assoc.collaboratorId + '-error'"
                        class="text-red-500"
                        role="alert"
                      >
                        {{ aeError }}
                      </small>
                    }
                  </div>
                </div>
                      }
                    </div>
                  }
              }
            </div>
          }
        </p-card>
      }
    }

    <!-- STEP 3 — Riepilogo e conferma -->
    @if (currentStepIndex() === 2) {
      @for (sol of sols(); track sol.solCodeId) {
        <p-card styleClass="mb-3">
          <div
            class="surface-100 border-round p-3 border-1 border-300"
            [class.mb-3]="isRecapOpen(sol)"
          >
            <div class="flex justify-content-between align-items-center gap-2">
              <button
                type="button"
                class="flex align-items-center gap-2 flex-1 border-none bg-transparent p-0 cursor-pointer text-left"
                (click)="toggleRecap(sol)"
                [attr.aria-expanded]="isRecapOpen(sol)"
                [attr.aria-controls]="'sec2-' + sol.solCodeId"
                [attr.aria-label]="(isRecapOpen(sol) ? 'Comprimi' : 'Espandi') + ' codice SOL ' + sol.code"
              >
                <i
                  class="pi"
                  [ngClass]="isRecapOpen(sol) ? 'pi-chevron-down' : 'pi-chevron-right'"
                  aria-hidden="true"
                ></i>
                <h4 class="text-lg font-semibold m-0 sol-code">
                  Codice SOL {{ sol.code }}
                  @if (sol.denomination) { <span class="font-normal text-600"> — {{ sol.denomination }}</span> }
                </h4>
              </button>
              <span class="flex-shrink-0">
                <p-tag [value]="statusLabel(sol.status)" severity="secondary" />
              </span>
            </div>
          </div>

          @if (isRecapOpen(sol)) {
            <div [id]="'sec2-' + sol.solCodeId" class="sol-section-body">
              @if (solLocation(sol)) {
                <p class="sol-location">
                  <i class="pi" [ngClass]="solLocationIcon(sol)" aria-hidden="true"></i>
                  <span class="visually-hidden">{{ solLocationLabel(sol) }}</span>{{ solLocation(sol) }}
                </p>
              }
              <div class="grid">
                <div class="field col-12 md:col-6">
                  <small class="text-600 block mb-1 font-bold">Legale Rappresentante</small>
                  <div>
                    {{ sol.holderLastName }} {{ sol.holderFirstName }}
                    @if (sol.holderFiscalCode) { <span class="text-600">({{ sol.holderFiscalCode }})</span> }
                  </div>
                </div>
                <div class="field col-12 md:col-6">
                  <small class="text-600 block mb-1 font-bold">Mail PERSONALE per accedere ai sistemi gestionali AXA</small>
                  <div>{{ sol.principalEmail || '—' }}</div>
                </div>
              </div>
              <div class="mt-3">
                <small class="text-600 block mb-2 font-bold">Associazioni ({{ selectedCount(sol) }})</small>
                @if (selectedCount(sol) === 0) {
                  <p class="assoc-cards__empty">Nessuna associazione</p>
                } @else {
                  <div class="assoc-cards">
                    @for (assoc of sol.associations; track assoc.collaboratorId) {
                      @if (assoc.selected) {
                        <div class="assoc-card">
                          <div class="assoc-card__person">
                            <span class="assoc-card__avatar" aria-hidden="true"><i class="pi pi-user"></i></span>
                            <div>
                              <div class="assoc-card__name">
                                {{ personFullName(assoc) }}
                                @if (assoc.fiscalCode) { <span class="assoc-card__fc">({{ assoc.fiscalCode }})</span> }
                              </div>
                              <div class="assoc-card__meta">
                                <p-tag [value]="personKindLabel(assoc)" severity="secondary" />
                                @if (personRole(assoc); as role) {
                                  <span class="assoc-card__role">{{ role }}</span>
                                }
                              </div>
                            </div>
                          </div>
                          <div class="assoc-card__mail">
                            <span class="k">Mail PERSONALE per accedere ai sistemi gestionali AXA</span>
                            <span class="v">{{ assoc.accessEmail || '—' }}</span>
                          </div>
                        </div>
                      }
                    }
                  </div>
                }
              </div>
            </div>
          }
        </p-card>
      }

      @if (!canConfirm()) {
        <p-message severity="warn" styleClass="w-full mb-3">
          Alcuni dati obbligatori non sono compilati. Torna agli step precedenti per completarli.
        </p-message>
      }
    }

    <!-- Azioni -->
    <div class="mfa-actions">
      <div class="left">
        @if (currentStepIndex() > 0) {
          <button
            pButton
            type="button"
            label="Indietro"
            icon="pi pi-arrow-left"
            class="p-button-outlined"
            (click)="back()"
            [disabled]="isSaving() || isConfirming()"
          ></button>
        }
      </div>
      <div class="right">
        <button
          pButton
          type="button"
          label="Salva bozza"
          icon="pi pi-save"
          class="p-button-outlined"
          (click)="saveDraft()"
          [loading]="isSaving()"
          [disabled]="isConfirming()"
        ></button>
        @if (currentStepIndex() < 2) {
          <button
            pButton
            type="button"
            label="Avanti"
            icon="pi pi-arrow-right"
            iconPos="right"
            (click)="next()"
            [disabled]="isSaving() || isConfirming()"
          ></button>
        } @else {
          <button
            pButton
            type="button"
            label="Conferma e invia"
            icon="pi pi-check"
            severity="success"
            (click)="confirm()"
            [loading]="isConfirming()"
            [disabled]="isSaving()"
          ></button>
        }
      </div>
    </div>
  }
</div>
